import { baseQuantity, baseUnitOf } from '../product/unit.js'
import type { Unit } from '../product/unit.js'

/**
 * O tamanho que o cupom imprime, e se ele bate com o do produto sugerido.
 *
 * ## O defeito que isto fecha
 *
 * Na revisão de nota, "Aceitar as sugestões" confirmava em bloco tudo o que a
 * leitura tinha casado — e a crítica da decisão 302 achou, no próprio demo,
 * uma "Coca-Cola lata **350ml**" sugerida para um cupom que diz
 * `REF COCA S/AC LA **310ML**`. Aceita às cegas, ela grava o preço da lata de
 * 310 no histórico da de 350, e o "quanto custa por litro" — a pergunta que o
 * catálogo existe para responder — passa a mentir sem ninguém ver.
 *
 * ## Só acusa quando os DOIS lados dizem um tamanho
 *
 * Pão francês, sacola, hortifrúti a granel: o cupom não imprime tamanho, ou o
 * produto não tem um. Acusar divergência ali tiraria do lote a metade de toda
 * nota de mercado, e um alarme que toca sempre ensina a ignorar o alarme.
 */

const UNIDADE_DO_CUPOM: Readonly<Record<string, Unit>> = {
  ML: 'ml',
  L: 'l',
  LT: 'l',
  LTS: 'l',
  G: 'g',
  GR: 'g',
  KG: 'kg',
}

/**
 * Número colado (ou quase) a uma unidade de medida, no fim de uma palavra.
 *
 * O `(?<![\dX])` é o que faz `6X350ML` dar 350 e não 6: num fardo o PDV
 * escreve a quantidade, um X, e o tamanho da UNIDADE — que é o que o produto
 * guarda em `size`.
 */
const TAMANHO = /(?<![\d,.])(\d+(?:[.,]\d+)?)\s?(ML|LTS|LT|L|KG|GR|G)\b/gi

/** O último tamanho impresso na descrição, ou `null` quando não há. */
export function tamanhoNoTexto(texto: string): { size: number; unit: Unit } | null {
  let achado: { size: number; unit: Unit } | null = null
  for (const m of texto.matchAll(TAMANHO)) {
    const unit = UNIDADE_DO_CUPOM[(m[2] ?? '').toUpperCase()]
    if (unit === undefined) continue
    achado = { size: Number((m[1] ?? '').replace(',', '.')), unit }
  }
  return achado
}

/**
 * O tamanho do cupom e o do produto são medidas diferentes?
 *
 * Compara na unidade BASE — 1,5L e 1500ml são a mesma garrafa —, e unidades de
 * natureza diferente (gramas contra mililitros) contam como divergência:
 * um café de 500G sugerido para um produto de 500ml é o produto errado.
 */
export function tamanhoDiverge(
  texto: string,
  produto: { size: number | null; unit: Unit | null },
): boolean {
  const doCupom = tamanhoNoTexto(texto)
  if (doCupom === null || produto.size === null || produto.unit === null) return false
  if (baseUnitOf(doCupom.unit) !== baseUnitOf(produto.unit)) return true
  // **Meia unidade base de folga**, e não igualdade: `2.01 * 1000` é
  // `2009.9999999999998` no JavaScript, e `!==` acusaria um galão de 2,01L
  // contra o produto de 2010ml. Meio mililitro (ou meio grama) está abaixo de
  // qualquer diferença que um PDV imprima entre dois produtos.
  const a = baseQuantity(doCupom.size, doCupom.unit, 1)
  const b = baseQuantity(produto.size, produto.unit, 1)
  return Math.abs(a - b) >= 0.5
}
